/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectVisualFilters,
  selectPmsSync,
  selectTargetDate,
  selectPaceHorizon,
  selectHotelName,
  selectEnableHotelGroups,
  selectLocalCurrency,
  selectBreakdownDetails,
} from 'modules/dashboard/selectors';
import { getSelectedBreakdown } from 'modules/dashboard/functions';
import {
  selectPaceAlignmentToggle,
  selectPaceDateComparison,
  selectPaceSymmetricComparisonToggle,
  selectPercentage,
  selectIsPaceDetailed,
  selectPaceChartBreakdown,
  selectPaceFocusOn,
  selectPaceLagAligned,
  selectPaceCumulativeView,
  selectIsPrimary,
  selectSymmetricComparisonIsDisplay,
  selectIsForecast,
  selectRevenueIsDisplayPS,
  selectOccupiedRoomsIsDisplayPS,
  selectAdrIsDisplayPS,
  selectRevenueIsDisplayPT,
  selectOccupiedRoomsIsDisplayPT,
  selectAdrIsDisplayPT,
  selectCxlIsDisplayPT,
  selectCxlIsDisplayPS,
  selectPaceBookingPaceWindow,
  selectPaceBookingDate,
  selectEnableSecondaryHotelGroups,
  selectNormalizedView,
  selectHotelFocusOn,
  selectActivePaceTab,
  selectRevenueIsDisplayPH,
  selectOccupiedRoomsIsDisplayPH,
  selectAdrIsDisplayPH,
  selectCxlIsDisplayPH,
  selectTabularView,
  selectCxlCountIsDisplay,
  selectIsPaceSegmentTimeDeAggregate,
} from '../selectors';
import { paceActions } from '../slice';

import { generatePaceShareUrl, getFocusOnFilter } from '../../inner-filter/functions';

/**
 * Custom hook to generate share url in pace tab
 */
const usePaceShareUrl = () => {
  const dispatch = useDispatch();
  //
  const targetDate = useSelector(selectTargetDate);
  const hotelId = useSelector(selectHotelName);
  const visualFilters = useSelector(selectVisualFilters);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const paceHorizon = useSelector(selectPaceHorizon);
  const focusOn = useSelector(selectPaceFocusOn);
  const chartBreakdown = useSelector(selectPaceChartBreakdown);
  const isDetailed = useSelector(selectIsPaceDetailed);
  const lagAligned = useSelector(selectPaceLagAligned);
  const isCumulative = useSelector(selectPaceCumulativeView);
  const enableHotelGroups = useSelector(selectEnableHotelGroups);
  const dateComparison = useSelector(selectPaceDateComparison);
  const percentage = useSelector(selectPercentage);
  const symmetricComparisonToggle = useSelector(selectPaceSymmetricComparisonToggle);
  const alignmentToggle = useSelector(selectPaceAlignmentToggle);
  const compareWindowCollapseToggle = useSelector(selectSymmetricComparisonIsDisplay);
  const isPrimary = useSelector(selectIsPrimary);
  const isForecast = useSelector(selectIsForecast);
  const paceWindowSelection = useSelector(selectPaceBookingPaceWindow);
  const bookingDate = useSelector(selectPaceBookingDate);
  //
  const revenueIsDisplayPS = useSelector(selectRevenueIsDisplayPS);
  const occupiedRoomsIsDisplayPS = useSelector(selectOccupiedRoomsIsDisplayPS);
  const adrIsDisplayPS = useSelector(selectAdrIsDisplayPS);
  const cxlIsDisplayPS = useSelector(selectCxlIsDisplayPS);
  const cxlCountIsDisplay = useSelector(selectCxlCountIsDisplay);
  //
  const revenueIsDisplayPT = useSelector(selectRevenueIsDisplayPT);
  const occupiedRoomsIsDisplayPT = useSelector(selectOccupiedRoomsIsDisplayPT);
  const adrIsDisplayPT = useSelector(selectAdrIsDisplayPT);
  const cxlIsDisplayPT = useSelector(selectCxlIsDisplayPT);
  //
  const revenueIsDisplayPH = useSelector(selectRevenueIsDisplayPH);
  const occupiedRoomsIsDisplayPH = useSelector(selectOccupiedRoomsIsDisplayPH);
  const adrIsDisplayPH = useSelector(selectAdrIsDisplayPH);
  const cxlIsDisplayPH = useSelector(selectCxlIsDisplayPH);
  //
  const activeTab = useSelector(selectActivePaceTab);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  //
  const tabularView = useSelector(selectTabularView);
  //
  const localCurrency = useSelector(selectLocalCurrency);
  const isPaceSegmentTimeDeAggregate = useSelector(selectIsPaceSegmentTimeDeAggregate);
  const breakdownData = useSelector(selectBreakdownDetails);
  // focus on option list of selected breakdown
  const focusOnList = useMemo(
    () =>
      getFocusOnFilter(
        breakdownData,
        getSelectedBreakdown(visualFilters, chartBreakdown, isDetailed)
      ),
    [breakdownData, visualFilters, chartBreakdown, isDetailed]
  );

  // Triggers when filter values dependencies changes
  useEffect(() => {
    if (!targetDate && !hotelId && !paceHorizon) return;
    // Generate pace widget filter url
    const paceWidgetUrl = generatePaceShareUrl(
      pmsSyncToggle,
      focusOn,
      lagAligned,
      isCumulative,
      visualFilters,
      targetDate,
      hotelId,
      paceHorizon,
      enableHotelGroups,
      hotelId,
      percentage,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      chartBreakdown,
      isDetailed,
      compareWindowCollapseToggle,
      isPrimary,
      isForecast,
      revenueIsDisplayPS,
      cxlCountIsDisplay,
      occupiedRoomsIsDisplayPS,
      adrIsDisplayPS,
      cxlIsDisplayPS,
      revenueIsDisplayPT,
      occupiedRoomsIsDisplayPT,
      adrIsDisplayPT,
      cxlIsDisplayPT,
      paceWindowSelection,
      bookingDate,
      hotelFocusOn,
      enableSecondaryHotelGroups,
      normalizedViewToggle,
      activeTab,
      revenueIsDisplayPH,
      occupiedRoomsIsDisplayPH,
      adrIsDisplayPH,
      cxlIsDisplayPH,
      tabularView,
      localCurrency,
      isPaceSegmentTimeDeAggregate,
      focusOnList
    );
    //
    dispatch(paceActions.setPaceWidgetShareUrl(paceWidgetUrl));
  }, [
    pmsSyncToggle,
    focusOn,
    lagAligned,
    isCumulative,
    visualFilters,
    targetDate,
    hotelId,
    paceHorizon,
    enableHotelGroups,
    percentage,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    paceHorizon,
    chartBreakdown,
    isDetailed,
    compareWindowCollapseToggle,
    isPrimary,
    isForecast,
    revenueIsDisplayPS,
    cxlCountIsDisplay,
    occupiedRoomsIsDisplayPS,
    adrIsDisplayPS,
    cxlIsDisplayPS,
    revenueIsDisplayPT,
    occupiedRoomsIsDisplayPT,
    adrIsDisplayPT,
    cxlIsDisplayPT,
    paceWindowSelection,
    bookingDate,
    hotelFocusOn,
    enableSecondaryHotelGroups,
    normalizedViewToggle,
    activeTab,
    revenueIsDisplayPH,
    occupiedRoomsIsDisplayPH,
    adrIsDisplayPH,
    cxlIsDisplayPH,
    tabularView,
    localCurrency,
    isPaceSegmentTimeDeAggregate,
    focusOnList,
  ]);
};
//
export default usePaceShareUrl;
